import { ChevronDown, MapPinOff, MapPinPlus, RotateCcw, UserMinus } from "lucide-react";

import type { CommandRelation, CompositionUnit, UnitRole } from "../../api/types";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "../../components/Menu";
import { IconButton } from "../../components/ui";
import { COMMAND_LABELS, COMMAND_RELATIONS, isCompany } from "../../company";
import { useGenericEnemy } from "../../enemyDisplay";
import { unitDesignation } from "../../i18n/designation";
import { t } from "../../i18n/i18n";
import { useDeployment } from "../../map/units/useDeployment";
import { defensiveRoles } from "../../map/units/propagation";
import { amiRoles, autoOption, companyRoles, defaultMark, roleOption, roleToSend, shownRole, UNIT_ROLES } from "./roles";
import { canChooseRole, canRemove, offTemplate, type SideView } from "./rows";

/** Menu d'une ligne d'unité du dossier (ordre de bataille de la carte, vue Manœuvre ; docs/18 §6.1) : « Rôle dans ce
 *  dossier » (les sept rôles, et « Automatique » côté ami, chacun avec sa correspondance dans l'OPO), « Placer au
 *  centre de la carte » pour une unité à placer, « Retirer de la carte » pour une unité posée depuis la liste,
 *  « Retirer du dossier ». Les écritures sont celles de l'appelant (`useCompositionWrites`), qui vérifie le droit
 *  d'écrire et désigne l'action attendue sinon. */
export function UnitRowMenu({
  view,
  unit,
  open,
  onOpenChange,
  busy,
  draggable,
  onRole,
  onPlaceCenter,
  onUnplace,
  onRemove,
  onCommand,
}: {
  view: SideView;
  unit: CompositionUnit;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  draggable: boolean;
  onRole: (role: UnitRole | null) => void;
  onPlaceCenter?: () => void;
  onUnplace?: () => void;
  onRemove?: () => void;
  /** Unité reçue par un S/GTIA (docs/21, décision 5) : TACON ou OPCON. */
  onCommand?: (command: CommandRelation) => void;
}) {
  const roles = canChooseRole(view, unit);
  // Relation de commandement : seulement pour une unité reçue d'un dossier de compagnie (le serveur publie `command`).
  const command = unit.command && onCommand ? unit.command : null;
  // Face à l'ennemi générique (registre B39), une unité par défaut n'a pas de « rôle de référence ».
  const neutral = useGenericEnemy();
  const { data: deployment } = useDeployment();
  const company = isCompany(deployment?.level);
  const removable = canRemove(view, unit) && Boolean(onRemove);
  const unplaceable = offTemplate(unit) && Boolean(onUnplace);
  if (!roles && !removable && !(draggable && onPlaceCenter) && !unplaceable && !command) return null;
  const choose = (role: UnitRole | null) => {
    const next = roleToSend(role, unit.role_default);
    const current = unit.role_chosen ? unit.role : null;
    if (next !== current) onRole(next);
  };
  const friendly = unit.side === "AMI";
  // Rôles d'une défense (registre A126) : ni reconnaissance ; 1er et 2e échelons défensifs, renforts.
  const defensive = friendly && defensiveRoles(deployment);
  return (
    <Menu
      open={open}
      onClose={() => onOpenChange(false)}
      align="end"
      width={friendly ? 300 : 340}
      portal
      ariaLabel={t("Actions de {unit}", { unit: unitDesignation(unit.label) })}
      trigger={
        <IconButton
          size="xs"
          data-row-action=""
          disabled={busy}
          title={t("Rôle et composition")}
          aria-label={t("Rôle et composition de {unit}", { unit: unitDesignation(unit.label) })}
          onClick={() => onOpenChange(!open)}
        >
          <ChevronDown size={14} />
        </IconButton>
      }
    >
      {/* Unité reçue par un S/GTIA : sa relation de commandement d'abord, choisie unité par unité (décision 5). */}
      {command && (
        <>
          <MenuLabel>{t("Reçue en renforcement")}</MenuLabel>
          {COMMAND_RELATIONS.map((relation) => (
            <MenuItem
              key={relation}
              label={COMMAND_LABELS[relation].label}
              hint={t(COMMAND_LABELS[relation].hint)}
              selected={command === relation}
              onSelect={() => relation !== command && onCommand?.(relation)}
            />
          ))}
        </>
      )}
      {roles && (
        <>
          {command && <MenuSeparator />}
          <MenuLabel>{t("Rôle dans ce dossier")}</MenuLabel>
          {friendly && unit.role_default === null && (
            <MenuItem label={autoOption(unit, company, defensive)} selected={unit.role === null} onSelect={() => choose(null)} />
          )}
          {/* Niveau compagnie (docs/21 §4) : seulement les rôles qui ont un emplacement dans le dispositif du S/GTIA,
              nommés comme dans son ordre (appui, pion de tête). */}
          {amiRoles(company && friendly ? companyRoles(deployment?.company?.roles) : UNIT_ROLES, defensive).map((role) => {
            const option = roleOption(unit.side, role, company && friendly, defensive);
            const mark = role === unit.role_default ? defaultMark(unit, neutral) : null;
            return (
              <MenuItem
                key={role}
                label={option.label}
                hint={[option.hint, mark].filter(Boolean).join(" · ") || undefined}
                selected={shownRole(unit.role, defensive) === role}
                onSelect={() => choose(role)}
              />
            );
          })}
          {unit.role_chosen && (
            <>
              <MenuSeparator />
              <MenuItem
                icon={RotateCcw}
                label={
                  unit.origin === "odb"
                    ? t("Revenir au rôle de l'ordre de bataille")
                    : unit.origin === "reference" && !neutral
                      ? t("Revenir au rôle de référence")
                      : t("Revenir au rôle par défaut")
                }
                onSelect={() => onRole(null)}
              />
            </>
          )}
        </>
      )}
      {draggable && onPlaceCenter && (
        <>
          {(roles || command) && <MenuSeparator />}
          <MenuItem
            icon={MapPinPlus}
            label={t("Placer au centre de la carte")}
            hint={t("Au temps affiché ; glissez-la ensuite où il faut.")}
            onSelect={onPlaceCenter}
          />
        </>
      )}
      {unplaceable && (
        <>
          <MenuSeparator />
          <MenuItem
            icon={MapPinOff}
            label={t("Retirer de la carte")}
            hint={t("L'unité revient dans la liste des unités à placer.")}
            onSelect={onUnplace}
          />
        </>
      )}
      {removable && (
        <>
          <MenuSeparator />
          <MenuItem
            icon={UserMinus}
            label={t("Retirer du dossier")}
            hint={t("Dans ce dossier seulement : le catalogue et l'ordre de bataille restent inchangés.")}
            onSelect={onRemove}
          />
        </>
      )}
    </Menu>
  );
}
